/**
 * Pedagogisk panel som visas vid svaga resultat.
 *
 * Triggas när nettobesparing är låg eller payback är lång, INNAN
 * användaren hinner se detaljerade siffror som "−1 kr/år" och stänga
 * fliken. Förklarar vad batteriet faktiskt tjänar pengar på och visar
 * de hävstänger som kan ändra kalkylen.
 *
 * Aktionsknappar togglar direkt relevanta scenarier (stödtjänster,
 * effekttariff) så användaren ser direkt vad varje val gör med
 * besparingen. Inga falska defaults — bara transparent visning av vad
 * som spelar roll.
 */

import type { Scenario } from "../sim/types";

interface WeakResultPanelProps {
  netSavingsSek: number;
  paybackYears: number | undefined;
  lifetimeYears: number;
  scenario: Scenario;
  onScenarioChange: (scenario: Scenario) => void;
}

export default function WeakResultPanel({
  netSavingsSek,
  paybackYears,
  lifetimeYears,
  scenario,
  onScenarioChange,
}: WeakResultPanelProps) {
  const ancillaryEnabled = scenario.ancillary.enabled;
  const powerTariffEnabled =
    !!scenario.grid.powerTariff &&
    scenario.grid.powerTariff.pricePerKWPerMonth > 0 &&
    (scenario.grid.powerTariff.activateFromYear ?? 0) === 0;

  function toggleAncillary() {
    onScenarioChange({
      ...scenario,
      ancillary: { ...scenario.ancillary, enabled: !ancillaryEnabled },
    });
  }

  // Välj rubrik och tonläge baserat på hur dåligt resultatet är
  let headline: string;
  let intro: string;
  if (netSavingsSek < 0) {
    headline = "Kalkylen går minus med dessa antaganden";
    intro =
      "Det är inte ovanligt och inte fel. Batterier i svenska hushåll utan stödtjänster och utan effekttariff är ofta inte lönsamma på dagens spotpriser. Här är vad som faktiskt rör nålen.";
  } else if (paybackYears !== undefined && paybackYears > lifetimeYears) {
    headline = "Batteriet hinner inte betala sig";
    intro =
      "Återbetalningstiden är längre än batteriets livslängd med nuvarande inställningar. Det är fysik, inte fel i kalkylen. Två-tre faktorer kan ändra bilden helt.";
  } else {
    headline = "Begränsad besparing med dessa antaganden";
    intro =
      "Spotpris-arbitrage ensamt räcker sällan för att göra batterier självgående i Sverige. Det som verkligen rör nålen är stödtjänster och kommande effekttariffer.";
  }

  return (
    <aside className="weak-result-panel">
      <h3>{headline}</h3>
      <p>{intro}</p>

      <ul className="weak-result-actions">
        <li>
          <div className="weak-result-action-text">
            <strong>Stödtjänster (FCR-D)</strong>
            <span>
              Anslut batteriet till en aggregator (CheckWatt, Bydee, Tibber
              Smart Charging) som använder kapaciteten för att stabilisera
              elnätet. Typisk intäkt 1 000–2 000 kr per kW och år, minus
              aggregatorns andel.
            </span>
          </div>
          <button
            type="button"
            className={`weak-result-toggle ${
              ancillaryEnabled ? "is-active" : ""
            }`}
            onClick={toggleAncillary}
          >
            {ancillaryEnabled ? "Aktiverad" : "Aktivera"}
          </button>
        </li>

        <li>
          <div className="weak-result-action-text">
            <strong>Effekttariff</strong>
            <span>
              Många nätbolag planerar att införa effekttariff under 2026–2028.
              Då blir kapning av effekttoppar plötsligt värd flera tusen
              kr/år. Justera under Nätbolag i sidebaren för att modellera.
            </span>
          </div>
          <span
            className={`weak-result-status ${
              powerTariffEnabled ? "is-active" : ""
            }`}
          >
            {powerTariffEnabled ? "Aktiverad" : "Inte aktiv"}
          </span>
        </li>

        <li>
          <div className="weak-result-action-text">
            <strong>Grön Teknik-avdrag</strong>
            <span>
              50% av total kostnad (material + arbete) för batteri-
              installation, max 50 000 kr per person och år. Två ägare =
              max 100 000 kr. Ange ditt nettopris (efter avdrag) i
              "Totalt pris" i sidebaren — inte säljarens bruttopris.
            </span>
          </div>
        </li>

        <li>
          <div className="weak-result-action-text">
            <strong>Mindre batteri</strong>
            <span>
              Marginalnyttan minskar kraftigt över 7–8 kWh för normalhushåll
              utan stor solanläggning. Pröva 5 kWh i sidebaren — total
              besparing blir lägre men kr per investerad krona ofta bättre.
            </span>
          </div>
        </li>

        <li>
          <div className="weak-result-action-text">
            <strong>Vänta</strong>
            <span>
              Batteripriser sjunker 5–10% per år i Sverige. Att vänta 1–2 år
              kan göra samma installation 10–20% billigare.
            </span>
          </div>
        </li>
      </ul>
    </aside>
  );
}
